import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Mail, MessageSquare, Hash, Phone, Save, TestTube,
  CheckCircle, AlertTriangle, Loader2
} from "lucide-react";

const channels = [
  { key: "email", label: "Email (SendGrid)", icon: Mail, placeholder: "admin@charstan.com", field: "email", hint: "Requires SENDGRID_API_KEY in environment" },
  { key: "slack", label: "Slack Webhook", icon: Hash, placeholder: "https://hooks.slack.com/services/...", field: "slack_webhook", hint: "Create an Incoming Webhook in Slack" },
  { key: "teams", label: "Microsoft Teams", icon: MessageSquare, placeholder: "https://outlook.office.com/webhook/...", field: "teams_webhook", hint: "Create a Connector webhook in Teams" },
  { key: "whatsapp", label: "WhatsApp (Twilio)", icon: Phone, placeholder: "+1234567890", field: "whatsapp_number", hint: "Requires TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN" },
];

const NotificationSettingsPage = () => {
  const { t } = useTranslation('platform');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    axios.get(`${API}/notifications/settings`, { withCredentials: true })
      .then(res => setSettings(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/settings`, settings, { withCredentials: true });
      setTestResults(prev => ({ ...prev, save: "success" }));
      setTimeout(() => setTestResults(prev => { const n = { ...prev }; delete n.save; return n; }), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const testChannel = async (channel) => {
    setTesting(channel);
    try {
      const res = await axios.post(`${API}/notifications/test/${channel}`, {}, { withCredentials: true });
      setTestResults(prev => ({ ...prev, [channel]: res.data.status }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [channel]: "error" }));
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <QMSLayout title={t('platform:communications.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="logo-hex w-12 h-12 animate-pulse" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('platform:communications.title')}>
      <div className="space-y-6 max-w-3xl" data-testid="notification-settings-page">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white">Notification Settings</h2>
          <p className="text-[#8B95A5] text-sm mt-1">Configure channels for handoff alerts and compliance notifications</p>
        </div>

        {testResults.save === "success" && (
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-2 rounded text-sm" data-testid="save-success-msg">
            <CheckCircle size={16} /> Settings saved successfully
          </div>
        )}

        <div className="space-y-4">
          {channels.map((ch) => (
            <div key={ch.key} className="qms-card" data-testid={`channel-${ch.key}`}>
              <div className="qms-card-body">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded bg-[#181B23] flex items-center justify-center">
                    <ch.icon size={18} className="text-[#C9A84C]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium text-sm">{ch.label}</h3>
                    <p className="text-[#8B95A5] text-xs">{ch.hint}</p>
                  </div>
                  {testResults[ch.key] && (
                    <span className={`text-xs px-2 py-1 rounded ${testResults[ch.key] === "sent" ? "bg-emerald-500/10 text-emerald-400" : testResults[ch.key] === "mocked" ? "bg-amber-500/10 text-amber-400" : "bg-red-500/10 text-red-400"}`}>
                      {testResults[ch.key] === "mocked" ? "No credentials" : testResults[ch.key] === "sent" ? "Sent" : testResults[ch.key] === "error" ? "Error" : testResults[ch.key]}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={settings[ch.field] || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, [ch.field]: e.target.value }))}
                    className="flex-1 bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
                    placeholder={ch.placeholder}
                    data-testid={`input-${ch.key}`}
                  />
                  <button
                    onClick={() => testChannel(ch.key)}
                    disabled={testing === ch.key}
                    className="px-3 py-2 bg-[#181B23] text-[#8B95A5] border border-[rgba(255,255,255,0.1)] rounded text-sm hover:text-white hover:border-[rgba(255,255,255,0.2)] transition-colors flex items-center gap-1"
                    data-testid={`test-${ch.key}-btn`}
                  >
                    {testing === ch.key ? <Loader2 size={14} className="animate-spin" /> : <TestTube size={14} />}
                    Test
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-6 py-2.5 bg-[#C9A84C] text-[#0D1B2A] font-semibold rounded hover:bg-[#dfc06a] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Settings
          </button>
        </div>

        {/* Info Section */}
        <div className="qms-card" data-testid="notification-info">
          <div className="qms-card-body text-sm text-[#8B95A5] space-y-2">
            <h4 className="text-white font-medium flex items-center gap-2"><AlertTriangle size={14} className="text-[#C9A84C]" /> How Notifications Work</h4>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>Handoff Alerts</strong> &mdash; Sent when the AI chatbot detects a specialist handoff request</li>
              <li><strong>Compliance Alerts</strong> &mdash; Automatically generated daily for audit deadlines, overdue tasks, and stale findings</li>
              <li><strong>Channels</strong> &mdash; Configure one or more channels. All configured channels receive notifications simultaneously</li>
              <li><strong>Credentials Required</strong> &mdash; Email needs SENDGRID_API_KEY in backend .env; SMS needs TWILIO credentials. Slack and Teams use the webhook URLs you enter above.</li>
            </ul>
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default NotificationSettingsPage;
